import React, { useState, useEffect, useMemo, useCallback } from "react";
import { format, differenceInMinutes, formatDistanceToNow } from "date-fns";
import { Pencil, Clock, Calendar, TrendingUp, Timer, Users, Moon, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import TimeTrackingCalendarCard from './TimeTrackingCalendarCard';
import ManualTimeEntryDialog from './ManualTimeEntryDialog';
import { DimmingProvider, useDimmingContext } from '@/context/DimmingContext';
import { BaseShiftCalendar } from '@/components/calendar-core';
import { calculateSingleDayDisplayHours } from '@/lib/scheduling/single-day-display-hours';
import UnifiedShiftProvider from '@/components/shifts/context/UnifiedShiftProvider';
import { useAuth } from '@/context/AuthContext';
import { useFeatureGating } from '@/hooks/business/useFeatureGating';
import { useDotyposAttendanceSync } from '@/hooks/integrations/useDotyposAttendanceSync';
import { RefreshCw } from 'lucide-react';
import TimeTrackingEnableWizard from './TimeTrackingEnableWizard';
import TimeTrackingActivityLogCard from './TimeTrackingActivityLogCard';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/ui/use-toast";

import { useAssignEmployee } from '@/hooks/scheduling/useShiftMutations';
import { useEmployees } from '@/hooks/employees';
import { UnifiedShiftService } from '@/lib/scheduling/unified-shift-service';
import { processShiftsToDisplayData } from '@/lib/attendance/time-tracking-utils';
import { ShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import {
  TimeTrackingTabProps,
  EmployeeShiftDisplayData
} from '@/types/time-tracking';
import { DayShift } from '@/types';
import { useUnifiedShift } from '@/components/shifts/context/UnifiedShiftProvider';
import { useBusinessTime } from '@/hooks/ui/useBusinessTime';



// Skeleton component for table rows during loading - compact version
const TableRowSkeleton: React.FC = () => (
  <TableRow className="h-10">
    <TableCell className="font-medium p-1 text-center">
      <Skeleton className="h-3 w-20 mx-auto" />
    </TableCell>
    <TableCell className="p-1 text-center">
      <Skeleton className="h-3 w-24 mx-auto" />
    </TableCell>
    <TableCell className="p-1 text-center">
      <Skeleton className="h-3 w-16 mx-auto" />
    </TableCell>
    <TableCell className="p-1 text-center">
      <Skeleton className="h-3 w-14 mx-auto" />
    </TableCell>
    <TableCell className="px-1 py-1 text-center">
      <Skeleton className="h-5 w-16 rounded-full mx-auto" />
    </TableCell>
  </TableRow>
);

const TimeTrackingTabContent: React.FC<TimeTrackingTabProps> = ({ business, loadingData }) => {
  const [employeeShiftData, setEmployeeShiftData] = useState<EmployeeShiftDisplayData[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { isFreePlan } = useFeatureGating();
  const { isSyncing, syncAttendanceWithShifts } = useDotyposAttendanceSync();

  // Activity log retention: 7 days for free/basic, 30 days for premium
  const activityLogRetentionDays = isFreePlan() ? 7 : 30;

  // Get business timezone from centralized source
  const { businessTimezone } = useBusinessTime();
  
  // Get display hours using new smart system
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  
  // Get opening hours from business data (now included in business object)
  const businessOpeningHours = business?.opening_hours || [];

  const [isArrivalModalOpen, setIsArrivalModalOpen] = useState<boolean>(false);
  const [isDepartureModalOpen, setIsDepartureModalOpen] = useState<boolean>(false);
  const [selectedEmployeeShift, setSelectedEmployeeShift] = useState<EmployeeShiftDisplayData | null>(null);
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState<number>(0);

  const { hoveredShiftId, setHoveredShiftId, getListItemDimmingClass } = useDimmingContext();
  
  // Wrapper function for calendar hover that uses timetracking variant
  const handleCalendarShiftHover = (shiftId: string | null) => {
    setHoveredShiftId(shiftId, 'timetracking');
  };
  
  // Wrapper function for list hover that uses timetracking variant
  const handleListItemHover = (shiftId: string | null) => {
    setHoveredShiftId(shiftId, 'timetracking');
  };

  // Helper to get opening hours for selected date
  const getOpeningHoursForDate = (date: Date): { displayText: string; isClosed: boolean; is24Hours: boolean } => {
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = dayNames[date.getDay()];

    const todayHours = businessOpeningHours?.find(hour => hour.day_of_week === dayName);

    if (!todayHours || todayHours.is_closed_all_day) {
      return { displayText: "CLOSED", isClosed: true, is24Hours: false };
    }

    if (todayHours.is_24_hours) {
      return { displayText: "Open 24 Hours", isClosed: false, is24Hours: true };
    }

    if (todayHours.opening_time && todayHours.closing_time) {
      const openTime = todayHours.opening_time.substring(0, 5);
      const closeTime = todayHours.closing_time.substring(0, 5);
      return { displayText: `${openTime} - ${closeTime}`, isClosed: false, is24Hours: false };
    }

    return { displayText: "Hours not set", isClosed: false, is24Hours: false };
  };

  const { employees, loadingEmployees } = useEmployees({ businessId: business?.id || null });

  // Use UnifiedShiftService directly for consistent status calculation
  const [dayShifts, setDayShifts] = useState<DayShift[]>([]);
  const [loadingTimeTrackingData, setLoadingTimeTrackingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate hours summary metrics using raw dayShifts data (UTC dates)
  // This avoids timezone issues from parsing formatted strings
  const hoursSummary = useMemo(() => {
    // Format helper
    const formatHoursMinutes = (mins: number) => {
      const h = Math.floor(Math.abs(mins) / 60);
      const m = Math.abs(mins) % 60;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    // Guard: return defaults if data not loaded
    if (!dayShifts || dayShifts.length === 0) {
      return {
        totalScheduledHours: '00:00',
        totalWorkedHours: '00:00',
        overtimeHours: '00:00',
        employeesPresent: 0,
        totalEmployees: 0,
        lateCount: 0,
        totalLateMinutes: 0,
        punctualityRate: 100
      };
    }

    let totalScheduledMinutes = 0;
    let totalWorkedMinutes = 0;
    let perShiftOvertimeMinutes = 0;
    let employeesPresent = 0;
    let lateCount = 0;
    let totalLateMinutes = 0;

    // Filter to assigned shifts only
    const assignedShifts = dayShifts.filter(s => s.employeeId);

    assignedShifts.forEach((shift) => {
      // Use raw UTC dates directly - no parsing needed
      const scheduledMinutes = differenceInMinutes(shift.endTime, shift.startTime);
      totalScheduledMinutes += scheduledMinutes;

      // Check if employee clocked in
      if (shift.actualArrivalTime) {
        employeesPresent++;

        // Calculate late minutes from raw UTC times
        const arrivalDate = new Date(shift.actualArrivalTime);
        const scheduledStart = shift.startTime;
        if (arrivalDate > scheduledStart) {
          const lateMinutes = differenceInMinutes(arrivalDate, scheduledStart);
          if (lateMinutes > 0) {
            totalLateMinutes += lateMinutes;
            lateCount++;
          }
        }
      }

      // Calculate worked hours if clocked in/out
      if (shift.actualArrivalTime && shift.actualDepartureTime) {
        const arrivalDate = new Date(shift.actualArrivalTime);
        const departureDate = new Date(shift.actualDepartureTime);
        const workedMinutes = differenceInMinutes(departureDate, arrivalDate);
        totalWorkedMinutes += workedMinutes;

        // Per-shift overtime
        if (workedMinutes > scheduledMinutes) {
          perShiftOvertimeMinutes += (workedMinutes - scheduledMinutes);
        }
      } else if (shift.actualArrivalTime && !shift.actualDepartureTime) {
        // Still working - use current time
        const arrivalDate = new Date(shift.actualArrivalTime);
        const now = new Date();
        const workedMinutes = differenceInMinutes(now, arrivalDate);
        totalWorkedMinutes += workedMinutes;

        // Check overtime against scheduled end
        if (now > shift.endTime) {
          perShiftOvertimeMinutes += differenceInMinutes(now, shift.endTime);
        }
      }
    });

    const punctualityRate = assignedShifts.length > 0
      ? Math.round(((assignedShifts.length - lateCount) / assignedShifts.length) * 100)
      : 100;

    return {
      totalScheduledHours: formatHoursMinutes(totalScheduledMinutes),
      totalWorkedHours: formatHoursMinutes(totalWorkedMinutes),
      overtimeHours: formatHoursMinutes(perShiftOvertimeMinutes),
      employeesPresent,
      totalEmployees: assignedShifts.length,
      lateCount,
      totalLateMinutes,
      punctualityRate
    };
  }, [dayShifts]);

  const refetchTimeTrackingData = useCallback(async () => {
    if (!business?.id) {
      setDayShifts([]);
      setLoadingTimeTrackingData(false);
      return;
    }

    setLoadingTimeTrackingData(true);
    setError(null);

    try {
      // Use UnifiedShiftService - single source of truth for status
      const result = await UnifiedShiftService.fetchShiftsForDay(
        business.id,
        selectedDate,
        employees,
        businessTimezone
      );
      setDayShifts(result.shifts);
    } catch (err: any) {
      console.error('Error fetching time tracking data:', err);
      setError(err.message || "Failed to fetch time tracking data.");
      setDayShifts([]);
    } finally {
      setLoadingTimeTrackingData(false);
    }
  }, [business?.id, selectedDate, employees, businessTimezone]);

  // Fetch data when dependencies change
  useEffect(() => {
    refetchTimeTrackingData();
  }, [refetchTimeTrackingData]);

  // Use centralized mutation hook for employee assignment
  const assignEmployeeMutation = useAssignEmployee();

  // Wrapper function to adapt mutation to expected signature
  const updateEmployee = async (shiftId: string, employeeId: string | null) => {
    await assignEmployeeMutation.mutateAsync({ shiftId, employeeId });
    // Refresh local state after mutation
    refetchTimeTrackingData();
    setCalendarRefreshTrigger(prev => prev + 1);
  };

  // Use frontend display hours calculation
  const displayHoursRange = useMemo(() => {
    const shifts = dayShifts?.map(shift => ({
      startTime: format(shift.startTime, 'HH:mm'),
      endTime: format(shift.endTime, 'HH:mm')
    })) || [];

    return calculateSingleDayDisplayHours(
      shifts,
      businessOpeningHours || [],
      selectedDateStr
    );
  }, [dayShifts, businessOpeningHours, selectedDateStr]);

  useEffect(() => {
    if (loadingData || !business || !business.id || loadingEmployees) {
      setEmployeeShiftData([]);
      return;
    }

    try {
      const processedData = processShiftsToDisplayData(dayShifts, employees, business, businessTimezone);
      setEmployeeShiftData(processedData);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      toast.error("Failed to process time tracking data.", { description: errorMessage });
    }
  }, [business, loadingData, selectedDate, dayShifts, loadingTimeTrackingData, employees, loadingEmployees]);

  const handleTimeEntrySave = () => {
    refetchTimeTrackingData();
    setCalendarRefreshTrigger(prev => prev + 1);
  };

  const handleOpenArrivalModal = (data: EmployeeShiftDisplayData) => {
    setSelectedEmployeeShift(data);
    setIsArrivalModalOpen(true);
  };

  const handleOpenDepartureModal = (data: EmployeeShiftDisplayData) => {
    setSelectedEmployeeShift(data);
    setIsDepartureModalOpen(true);
  };

  // Calculate display start/end times using frontend calculation
  const { displayStartTime, displayEndTime } = useMemo(() => {
    return {
      displayStartTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), displayHoursRange.start, 0),
      displayEndTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(),
        displayHoursRange.end >= 24 ? 23 : displayHoursRange.end,
        displayHoursRange.end >= 24 ? 59 : 0
      )
    };
  }, [displayHoursRange, selectedDate]);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">

        {/* Attendance Overview Card - Top Left */}
        <Card className="md:col-span-1 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Attendance Overview</CardTitle>
            </div>
            {/* Status Badges */}
            <div className="flex items-center gap-2">
              {/* POS Connection Status with Sync Info */}
              {business?.dotypos_enabled ? (
                <>
                  <Badge
                    className={`flex items-center gap-1 text-xs ${
                      business.last_attendance_sync_at &&
                      (new Date().getTime() - new Date(business.last_attendance_sync_at).getTime()) < 3600000
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    }`}
                    title={business.last_attendance_sync_at
                      ? `Last sync: ${format(new Date(business.last_attendance_sync_at), 'PPp')}`
                      : 'Never synced'}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    POS {business.last_attendance_sync_at
                      ? formatDistanceToNow(new Date(business.last_attendance_sync_at), { addSuffix: true })
                      : '(never synced)'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => syncAttendanceWithShifts(business.id, 'match_existing', 'incremental')}
                    disabled={isSyncing}
                    className="h-6 w-6 p-0"
                    title="Sync attendance from POS"
                  >
                    <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                  </Button>
                </>
              ) : (
                <Badge variant="outline" className="flex items-center gap-1 text-muted-foreground text-xs">
                  <XCircle className="w-3 h-3" />
                  No POS
                </Badge>
              )}
              {/* Opening Hours Badge */}
              {loadingTimeTrackingData ? (
                <Skeleton className="h-6 w-24" />
              ) : (
                <Badge
                  variant="secondary"
                  className={
                    getOpeningHoursForDate(selectedDate).isClosed
                      ? "bg-gray-600/80 text-white dark:bg-gray-400/80 dark:text-gray-900 hover:bg-gray-600/80"
                      : getOpeningHoursForDate(selectedDate).is24Hours
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 hover:bg-blue-100"
                        : "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 hover:bg-green-100"
                  }
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {getOpeningHoursForDate(selectedDate).displayText}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Main Metrics Grid - 2x2 */}
            <div className="grid grid-cols-2 gap-3">
              {/* Present / Total */}
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-card rounded-lg border">
                {loadingTimeTrackingData ? (
                  <>
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-6 w-10" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-950">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Present</p>
                      <p className="text-2xl font-bold">{hoursSummary.employeesPresent}/{hoursSummary.totalEmployees}</p>
                    </div>
                  </>
                )}
              </div>

              {/* On Time % */}
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-card rounded-lg border">
                {loadingTimeTrackingData ? (
                  <>
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-6 w-10" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`p-2 rounded-lg ${hoursSummary.punctualityRate >= 80 ? 'bg-green-100 dark:bg-green-950' : 'bg-amber-100 dark:bg-amber-950'}`}>
                      <CheckCircle2 className={`h-5 w-5 ${hoursSummary.punctualityRate >= 80 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">On Time</p>
                      <p className={`text-2xl font-bold ${hoursSummary.punctualityRate >= 80 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {hoursSummary.punctualityRate}%
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Late Arrivals */}
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-card rounded-lg border">
                {loadingTimeTrackingData ? (
                  <>
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-6 w-16" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`p-2 rounded-lg ${hoursSummary.lateCount > 2 ? 'bg-red-100 dark:bg-red-950' : 'bg-amber-100 dark:bg-amber-950'}`}>
                      <AlertCircle className={`h-5 w-5 ${hoursSummary.lateCount > 2 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Late</p>
                      <p className={`text-2xl font-bold ${hoursSummary.lateCount > 2 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {hoursSummary.lateCount}
                      </p>
                      {hoursSummary.totalLateMinutes > 0 && (
                        <p className="text-xs text-muted-foreground">({hoursSummary.totalLateMinutes} min)</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Overtime */}
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-card rounded-lg border">
                {loadingTimeTrackingData ? (
                  <>
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3 w-12" />
                      <Skeleton className="h-6 w-14" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-950">
                      <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Overtime</p>
                      <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{hoursSummary.overtimeHours}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Hours Summary Row */}
            <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span>Scheduled: <strong className="text-foreground">{hoursSummary.totalScheduledHours}</strong></span>
                <span>Worked: <strong className="text-foreground">{hoursSummary.totalWorkedHours}</strong></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Card - Top Right */}
        <TimeTrackingCalendarCard
          className="col-span-2"
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          business={business}
          refreshTrigger={calendarRefreshTrigger}
          dayShifts={dayShifts}
          loadingShifts={loadingTimeTrackingData}
          employees={employees}
          loadingEmployees={loadingEmployees}
          onShiftCreated={() => {
            refetchTimeTrackingData();
            setCalendarRefreshTrigger(prev => prev + 1);
          }}
        />

        {/* Schedule View - Bottom Left */}
        <div className="md:col-span-1 lg:col-span-2 h-full">
          <BaseShiftCalendar
            variant="timetracking"
            currentDisplayMonth={selectedDate}
            displayOnlyDay={selectedDate}
            shifts={dayShifts}
            workingHours={[]} // Pass empty array like ShiftManagementTab
            openingHours={businessOpeningHours} // Pass actual opening hours for business hours styling
            displayStartTime={displayStartTime} // Pass explicit start time from display hours calculation
            displayEndTime={displayEndTime} // Pass explicit end time from display hours calculation
            enableShiftAwareTimeRange={true} // Enable shift-aware time range like ShiftManagementTab
            businessId={business?.id}
            employees={employees}
            onShiftCreated={() => {
              refetchTimeTrackingData();
              setCalendarRefreshTrigger(prev => prev + 1);
            }}
            onDateSelect={setSelectedDate}
            refreshTrigger={calendarRefreshTrigger}
            loading={loadingTimeTrackingData}
            className="border rounded-lg shadow-sm h-full"
            hoveredShiftId={hoveredShiftId}
            onShiftHover={handleCalendarShiftHover}
            onAssignEmployee={updateEmployee}
          />
        </div>

        {/* Time Tracking Table - Bottom Right */}
        <div className="md:col-span-1 lg:col-span-2 h-full">
          {error ? (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <p className="text-destructive">Error: {error}</p>
              </CardContent>
            </Card>
          ) : loadingTimeTrackingData || employeeShiftData.length === 0 ? (
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Time Tracking</CardTitle>
                <p className="text-sm font-medium">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
              </CardHeader>
              <CardContent className="p-0">
                {loadingTimeTrackingData ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="h-10" style={{ height: '40px' }}>
                        <TableHead className="p-0 text-center" style={{ height: '40px' }}>Employee</TableHead>
                        <TableHead className="p-0 text-center" style={{ height: '40px' }}>Shift Time</TableHead>
                        <TableHead className="p-0 text-center" style={{ height: '40px' }}>Arrival Time</TableHead>
                        <TableHead className="p-0 text-center" style={{ height: '40px' }}>Departure Time</TableHead>
                        <TableHead className="p-0 text-center" style={{ height: '40px' }}>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from({ length: 2 }).map((_, index) => (
                        <TableRowSkeleton key={index} />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="pt-6 pb-6">
                    <p className="text-center text-muted-foreground">No shifts scheduled for {format(selectedDate, "EEEE, MMMM d, yyyy")}.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Time Tracking</CardTitle>
                <p className="text-sm font-medium">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="h-10" style={{ height: '40px' }}>
                      <TableHead className="p-0 text-center" style={{ height: '40px' }}>Employee</TableHead>
                      <TableHead className="p-0 text-center" style={{ height: '40px' }}>Shift Start - End</TableHead>
                      <TableHead className="p-0 text-center" style={{ height: '40px' }}>Arrival Time</TableHead>
                      <TableHead className="p-0 text-center" style={{ height: '40px' }}>Departure Time</TableHead>
                      <TableHead className="p-0 text-center" style={{ height: '40px' }}>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody style={{ paddingTop: '10px' }}>
                    {employeeShiftData.map((data) => (
                      <TableRow 
                        key={data.id}
                        className={`h-16 ${getListItemDimmingClass(data.shiftId, data.employeeId)}`}
                        onMouseEnter={() => handleListItemHover(data.shiftId)}
                        onMouseLeave={() => handleListItemHover(null)}
                      >
                        <TableCell className="font-medium p-0 text-center">
                          {data.employeeName}
                        </TableCell>
                        <TableCell className="p-0 text-center">
                          {data.scheduledStartTime} - {data.scheduledEndTime}
                        </TableCell>
                        <TableCell className="p-0 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {data.actualArrivalTime || (data.status === 'open_shift' ? 'N/A' : 'Not Arrived')}
                            {data.employeeId && data.status !== 'open_shift' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenArrivalModal(data)}
                                className="h-6 w-6"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-0 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {data.actualDepartureTime || 'N/A'}
                            {data.employeeId && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDepartureModal(data)}
                                className="h-6 w-6"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-1 py-0 text-center">
                          <div className="whitespace-nowrap">
                            <ShiftStatusBadge 
                              status={data.status} 
                              lateMinutes={data.lateMinutes} 
                              minutesEarly={data.minutesEarly}
                              size="sm"
                              showIcon={false}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Activity Log Card - Full Width Below Grid */}
      <TimeTrackingActivityLogCard
        businessId={business?.id || null}
        selectedDate={selectedDate}
        employees={employees}
        className="mt-4"
        retentionDays={activityLogRetentionDays}
        onShiftClick={(shiftId, shiftDate) => {
          // Navigate to the shift's date in the calendar
          setSelectedDate(shiftDate);
        }}
      />

      {/* Manual Time Entry Dialogs */}
      <ManualTimeEntryDialog
        isOpen={isArrivalModalOpen}
        onOpenChange={setIsArrivalModalOpen}
        selectedEmployeeShift={selectedEmployeeShift}
        selectedDate={selectedDate}
        businessId={business?.id || ''}
        eventType="arrival"
        onSave={handleTimeEntrySave}
      />
      
      <ManualTimeEntryDialog
        isOpen={isDepartureModalOpen}
        onOpenChange={setIsDepartureModalOpen}
        selectedEmployeeShift={selectedEmployeeShift}
        selectedDate={selectedDate}
        businessId={business?.id || ''}
        eventType="departure"
        onSave={handleTimeEntrySave}
      />
    </>
  );
};

const TimeTrackingTab: React.FC<TimeTrackingTabProps> = (props) => {
  const { timeTrackingEnabled } = useAuth();
  const businessId = props.business?.id || null;

  if (!timeTrackingEnabled) {
    return (
      <Card className="w-full" style={{ height: 'calc(100vh - 80px)' }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Time Tracking
          </CardTitle>
          <CardDescription>Track employee attendance and working hours</CardDescription>
        </CardHeader>
        <CardContent className="h-[calc(100%-60px)] overflow-y-auto">
          <TimeTrackingEnableWizard
            businessId={businessId}
            businessName={props.business?.name}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <UnifiedShiftProvider businessId={businessId}>
      <DimmingProvider>
        <TimeTrackingTabContent {...props} />
      </DimmingProvider>
    </UnifiedShiftProvider>
  );
};

export default TimeTrackingTab;
